import { Search, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

export interface SearchResult {
  id: string;
  title: string;
  subtitle: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  results: readonly SearchResult[];
  onSelect: (id: string) => void;
  label: string;
  placeholder: string;
  clearLabel: string;
  noResultsText: string;
  /** Announced to screen readers when the result list changes. */
  resultsCountText: string;
}

/**
 * Design `SearchField` (capsule on glass) with a results popover, wired as an ARIA combobox:
 * Arrow keys move, Enter picks (first result if none is highlighted), Escape closes then clears.
 */
export function SearchField({
  value,
  onChange,
  results,
  onSelect,
  label,
  placeholder,
  clearLabel,
  noResultsText,
  resultsCountText,
}: Props) {
  const uid = useId();
  const listId = `${uid}-list`;
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  const hasQuery = value.trim() !== "";
  const open = focused && hasQuery && !dismissed;

  useEffect(() => {
    setActive(-1);
    setDismissed(false);
  }, [value]);

  const pick = (id: string) => {
    onSelect(id);
    setDismissed(true);
    (document.activeElement as HTMLElement | null)?.blur();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && results.length > 0) {
      e.preventDefault();
      setDismissed(false);
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp" && results.length > 0) {
      e.preventDefault();
      setDismissed(false);
      setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter" && open) {
      const r = results[active >= 0 ? active : 0];
      if (r) {
        e.preventDefault();
        pick(r.id);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setDismissed(true);
      } else if (hasQuery) {
        e.preventDefault();
        onChange("");
      }
    }
  };

  return (
    <div
      ref={rootRef}
      className="relative"
      onBlur={(e) => {
        if (!rootRef.current?.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      <div className="ds-search ds-glass">
        <Search aria-hidden="true" strokeWidth={1.75} />
        <input
          type="search"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && active >= 0 ? `${uid}-opt-${active}` : undefined}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="search"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={onKeyDown}
        />
        {hasQuery ? (
          <button
            type="button"
            className="search-clear"
            aria-label={clearLabel}
            onClick={() => onChange("")}
          >
            <X size={18} strokeWidth={1.75} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <ul
        id={listId}
        role="listbox"
        aria-label={label}
        className="search-popover ds-glass"
        hidden={!open}
      >
        {results.map((r, i) => (
          <li
            key={r.id}
            id={`${uid}-opt-${i}`}
            role="option"
            aria-selected={i === active}
            className="search-option"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick(r.id)}
            onMouseMove={() => setActive(i)}
          >
            <span className="ds-text-headline">{r.title}</span>
            {r.subtitle ? (
              <span className="ds-text-subheadline text-label-secondary">{r.subtitle}</span>
            ) : null}
          </li>
        ))}
        {results.length === 0 ? (
          <li
            role="presentation"
            className="search-option search-option--empty ds-text-subheadline"
          >
            {noResultsText}
          </li>
        ) : null}
      </ul>
      <p className="sr-only" role="status">
        {open ? (results.length > 0 ? resultsCountText : noResultsText) : ""}
      </p>
    </div>
  );
}
